import { useMediaQuery, useDebouncedValue } from "@mantine/hooks";
import { Loader, Tabs, TextInput, Grid, Input, Modal, Button, NumberInput, ActionIcon, Pagination, Tooltip, Text } from "@mantine/core";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IconEdit, IconSearch, IconEye } from "@tabler/icons-react";
import moment from "moment";
import Navigation from "../../components/customer/sidebar/Navigation";
import { createB2BAPI } from "../../api/Interceptor";
import B2BTableGrid from "../../components/common/B2BTableGrid";
import notify from "../../utils/Notification";
import { DateInput } from "@mantine/dates";
import { useLoader } from "../../Context/LoaderContext";
import '@mantine/dates/styles.css';
import './VendorStockAvailability.css';

export default function MadeToOrder() {
    const isMobile = useMediaQuery("(max-width: 767px)");
    const isTablet = useMediaQuery("(min-width: 768px) and (max-width: 991px)");
    const navigate = useNavigate();
    const { showLoader, hideLoader } = useLoader();
    const userId = JSON.parse(localStorage.getItem('user'))?.userId;
    const B2B_API = useMemo(() => createB2BAPI(navigate), [navigate]);

    const [data, setData] = useState([]);
    console.log(data, "dta ")
    const [isLoading, setIsLoading] = useState(false);
    const [rowCount, setRowCount] = useState(0);
    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 5 });
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch] = useDebouncedValue(searchTerm, 500);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(new Date());
    const [vendorDetails, setVendorDetails] = useState(null);
    const [courierName, setCourierName] = useState("");
    const [swatchQuantity, setSwatchQuantity] = useState(0);
    // Modal state
    const [editModalOpened, setEditModalOpened] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [newCostPrice, setNewCostPrice] = useState(0);
    const [fabricLeadTime, setFabricLeadTime] = useState(0);
    const [dyeLotLeadTime, setDyeLotLeadTime] = useState(0);
    const [greigeLeadTime, setGreigeLeadTime] = useState(0);
    const [isUpdating, setIsUpdating] = useState(false);

    const fetchVendorDetails = async () => {
        try {
            const response = await B2B_API.get(`vendor/${userId}`).json();
            const details = response?.response || response;
            console.log("Vendor Details Fetched:", details);
            setVendorDetails(details);
        } catch (error) {
            console.error("Error fetching vendor details:", error);
        }
    };

    const handleEdit = (row) => {
        setSelectedOrder(row);
        setNewCostPrice(row.costPrice || 0);

        setCourierName(row.courierName || "");
        setSwatchQuantity(row.swatchQuantity || 0);

        setFabricLeadTime(row.fabricLeadTime || vendorDetails?.fabricLeadTime || 0);
        setDyeLotLeadTime(row.dyeLotLeadTime || vendorDetails?.dyeLotLeadTime || 0);

        const greigeVal =
            row.greigeLeadTime ||
            row.grayLeadTime ||
            vendorDetails?.greigeLeadTime ||
            vendorDetails?.grayLeadTime ||
            0;

        setGreigeLeadTime(greigeVal);

        setEditModalOpened(true);
    };

    useEffect(() => {
        if (userId) fetchVendorDetails();
    }, [userId]);

    const updateCostPrice = async () => {
        setIsUpdating(true);
        showLoader();
        try {
            const orderBody = {
                ...selectedOrder,
                costPrice: newCostPrice,
                courierName: courierName,
                swatchQuantity: swatchQuantity,
                fabricLeadTime: fabricLeadTime,
                dyeLotLeadTime: dyeLotLeadTime,
                greigeLeadTime: greigeLeadTime,
                madeToOrderVerified: true
            };
            await B2B_API.post('madeToOrder/update', { json: orderBody }).json();
            notify({
                success: true,
                error: false,
                title: "Updated Successfully",
                message: "Order updated."
            });
            setEditModalOpened(false);
            fetchMadeToOrder(); // Refresh data
            fetchVendorDetails(); // Refresh vendor defaults
        } catch (error) {
            console.error("Error updating details:", error);
            notify({
                error: true,
                success: false,
                title: "Update Failed",
                message: error?.message || "Something went wrong."
            });
        } finally {
            setIsUpdating(false);
            hideLoader();
        }
    };

    const columns = useMemo(() => [
        {
            header: 'Date',
            accessorKey: 'createdDate',
            size: 120,
            mantineTableHeadCellProps: { align: 'center' },
            mantineTableBodyCellProps: { align: 'center' },
            accessorFn: row => moment(row?.createdDate).format('DD/MM/YYYY')
        },
        {
            header: 'Customer',
            accessorFn: row => row?.customer?.companyName || 'N/A',
            size: 180,
            mantineTableHeadCellProps: { align: 'center' },
            mantineTableBodyCellProps: { align: 'center' }
        },
        {
            header: 'MTO ID',
            accessorKey: 'mtoEnquiryId',
            accessorFn: row => row?.mtoEnquiryId || 'N/A',
            size: 180,
            mantineTableHeadCellProps: { align: 'center' },
            mantineTableBodyCellProps: { align: 'center' }
        },
        {
            header: 'Product',
            accessorKey: 'productName',
            size: 180,
            mantineTableHeadCellProps: { align: 'center' },
            mantineTableBodyCellProps: { align: 'center' },
            cell: ({ row }) => {
                const name = row.original.productName || "";
                return (
                    <Tooltip label={name} withArrow>
                        <Text>
                            {name.length > 10 ? `${name.substring(0, 10)}...` : name}
                        </Text>
                    </Tooltip>
                );
            }
        },
        {
            header: 'Variant',
            accessorKey: 'variantName',
            size: 180,
            mantineTableHeadCellProps: { align: 'center' },
            mantineTableBodyCellProps: { align: 'center' },
            cell: ({ row }) => {
                const name = row.original.variantName || "";
                return (
                    <Tooltip label={name} withArrow>
                        <Text>
                            {name.length > 10 ? `${name.substring(0, 10)}...` : name}
                        </Text>
                    </Tooltip>
                );
            }
        },
        {
            header: 'UOM',
            accessorKey: 'uom',
            size: 180,
            mantineTableHeadCellProps: { align: 'center' },
            mantineTableBodyCellProps: { align: 'center' }
        },
        {
            header: 'Qty',
            accessorFn: row => `${row.quantity} ${row.uom}`,
            size: 100,
            mantineTableHeadCellProps: { align: 'center' },
            mantineTableBodyCellProps: { align: 'center' }
        },
        {
            header: 'Rolls',
            accessorKey: 'noOfRolls',
            size: 80,
            mantineTableHeadCellProps: { align: 'center' },
            mantineTableBodyCellProps: { align: 'center' }
        },
        {
            header: 'Cost Price',
            accessorFn: row => `₹ ${row.costPrice?.toFixed(2)}`,
            size: 100,
            mantineTableHeadCellProps: { align: 'center' },
            mantineTableBodyCellProps: { align: 'center' }
        },
        {
            header: 'Action',
            size: 100,
            mantineTableHeadCellProps: { align: 'center' },
            mantineTableBodyCellProps: { align: 'center' },
            cell: ({ row }) => {
                return (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: '0.5rem',
                            cursor: 'pointer',
                        }}
                        onClick={() => handleEdit(row.original)}
                    >
                        <IconEdit size={18} /> <span>{row?.original?.madeToOrderVerified ? "Verified" : "Not Verified"}</span>
                    </div>
                );
            }
        }
    ], [vendorDetails]);

    const fetchMadeToOrder = async () => {
        setIsLoading(true);
        showLoader();
        try {
            const formattedFromDate = fromDate ? moment(fromDate).format("DD/MM/YYYY") : "";
            const formattedToDate = toDate ? moment(toDate).format("DD/MM/YYYY") : "";

            const response = await B2B_API.get(`madeToOrder/getAll?vendorId=${userId}&page=${pagination.pageIndex}&size=${pagination.pageSize}&searchTerm=${debouncedSearch}&fromDate=${formattedFromDate}&toDate=${formattedToDate}`).json();

            const fetchedData = response?.response?.content || response?.response || [];
            setData(fetchedData);
            setRowCount(response?.response?.totalElements || fetchedData.length || 0);
        } catch (error) {
            console.error("Error fetching Made to Order data:", error);
            notify({
                error: true,
                success: false,
                title: "Error fetching Made to Order data"
            });
        } finally {
            setIsLoading(false);
            hideLoader();
        }
    };

    useEffect(() => {
        fetchMadeToOrder();
    }, [pagination.pageIndex, pagination.pageSize, debouncedSearch, fromDate, toDate]);

    const totalPages = Math.ceil(rowCount / pagination.pageSize);

    const renderMobileCards = () => {
        if (isLoading) return <div style={{ textAlign: "center", padding: "2rem" }}>Loading...</div>;
        if (!data || data.length === 0) return <div style={{ textAlign: "center", padding: "2rem", color: "#666" }}>No data found.</div>;

        return (
            <div className="vsa-mobile-cards">
                {data.map((item, index) => (
                    <div key={index} className="vsa-card">
                        <div className="vsa-card-header">
                            <span className="vsa-card-date">{moment(item?.createdDate).format("DD/MM/YYYY")}</span>
                            <span style={{ fontSize: "0.85rem", fontWeight: "bold", color: item?.madeToOrderVerified ? "#8BC53F" : "#ff7b00" }}>
                                {item?.madeToOrderVerified ? "Verified" : "Not Verified"}
                            </span>
                        </div>
                        <div className="vsa-card-body">
                            <div className="vsa-card-row">
                                <span className="vsa-card-label">Customer:</span>
                                <span className="vsa-card-value">{item?.customer?.companyName || 'N/A'}</span>
                            </div>
                            <div className="vsa-card-row">
                                <span className="vsa-card-label">Product:</span>
                                <Tooltip label={item.productName} withArrow>
                                    <span className="vsa-card-value">
                                        {item.productName?.length > 10
                                            ? `${item.productName.substring(0, 10)}...`
                                            : item.productName}
                                    </span>
                                </Tooltip>
                            </div>

                            <div className="vsa-card-row">
                                <span className="vsa-card-label">Variant:</span>
                                <Tooltip label={item.variantName} withArrow>
                                    <span className="vsa-card-value">
                                        {item.variantName?.length > 10
                                            ? `${item.variantName.substring(0, 10)}...`
                                            : item.variantName}
                                    </span>
                                </Tooltip>
                            </div>
                            <div className="vsa-card-row">
                                <span className="vsa-card-label">Qty & UOM:</span>
                                <span className="vsa-card-value">{item.quantity} {item.uom}</span>
                            </div>
                            <div className="vsa-card-row">
                                <span className="vsa-card-label">Rolls:</span>
                                <span className="vsa-card-value">{item.noOfRolls}</span>
                            </div>
                            <div className="vsa-card-row">
                                <span className="vsa-card-label">Cost Price:</span>
                                <span className="vsa-card-value">₹ {item.costPrice?.toFixed(2)}</span>
                            </div>
                        </div>
                        <div className="vsa-card-actions">
                            <Button
                                size="xs"
                                variant="light"
                                color="#8BC53F"
                                leftSection={<IconEdit size={14} />}
                                onClick={() => handleEdit(item)}
                            >
                                Edit
                            </Button>
                        </div>
                    </div>
                ))}
                {totalPages > 1 && (
                    <div className="vsa-pagination-mobile">
                        <Pagination
                            total={totalPages}
                            value={pagination.pageIndex + 1}
                            onChange={(page) => setPagination(p => ({ ...p, pageIndex: page - 1 }))}
                            color="#8BC53F"
                        />
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="view-my-page router-view-container">
            <div className="route-view">
                {!isMobile && !isTablet && <Navigation />}

                <div className="vsa-container">
                    <h2 className="vsa-title">Made to Order</h2>

                    <div className="vsa-filters">
                        <Input
                            placeholder="Search"
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.currentTarget.value)}
                            rightSection={<IconSearch size={16} />}
                        />

                        <DateInput
                            placeholder="From date"
                            value={fromDate}
                            onChange={setFromDate}
                            maxDate={toDate || new Date()}
                            clearable
                        />
                        <DateInput
                            placeholder="To date"
                            value={toDate}
                            onChange={setToDate}
                            minDate={fromDate}
                            maxDate={new Date()}
                            clearable
                        />
                        <Button
                            color="red"
                            variant="light"
                            disabled={!searchTerm?.trim() && !fromDate}
                            onClick={() => {
                                setSearchTerm("");
                                setFromDate(null);
                                setToDate(new Date());
                            }}
                        >
                            Clear
                        </Button>
                    </div>

                    {isMobile ? (
                        renderMobileCards()
                    ) : (
                        <B2BTableGrid
                            columns={columns}
                            data={data}
                            isLoading={isLoading}
                            rowCount={rowCount}
                            pagination={pagination}
                            setPagination={setPagination}
                            onPaginationChange={setPagination}
                            manualPagination={true}
                        />
                    )}
                </div>
            </div>

            <Modal
                opened={editModalOpened}
                onClose={() => setEditModalOpened(false)}
                title="Edit Order Details"
                centered
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div style={{ fontSize: '14px' }}>
                        <strong>Product:</strong>{" "}
                        <Tooltip label={selectedOrder?.productName} withArrow>
                            <Text span>
                                {selectedOrder?.productName?.length > 10
                                    ? `${selectedOrder.productName.substring(0, 10)}...`
                                    : selectedOrder?.productName}
                            </Text>
                        </Tooltip>

                        <br />

                        <strong>Variant:</strong>{" "}
                        <Tooltip label={selectedOrder?.variantName} withArrow>
                            <Text span>
                                {selectedOrder?.variantName?.length > 10
                                    ? `${selectedOrder.variantName.substring(0, 10)}...`
                                    : selectedOrder?.variantName}
                            </Text>
                        </Tooltip>
                    </div>
                    <NumberInput
                        label="Cost Price"
                        value={newCostPrice}
                        onChange={(val) => setNewCostPrice(val)}
                        precision={2}
                        min={0}
                    />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        <NumberInput
                            label="Fabric Lead Time (Days)"
                            value={fabricLeadTime}
                            onChange={(val) => setFabricLeadTime(val)}
                            min={0}
                            readOnly={false}
                        />
                        <NumberInput
                            label="Dye Lot Lead Time (Days)"
                            value={dyeLotLeadTime}
                            onChange={(val) => setDyeLotLeadTime(val)}
                            min={0}
                            readOnly={false}
                        />
                        <NumberInput
                            label="Greige Lead Time (Days)"
                            value={greigeLeadTime}
                            onChange={(val) => setGreigeLeadTime(val)}
                            min={0}
                            readOnly={false}
                        />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
                        <Button
                            color="#8BC53F"
                            onClick={updateCostPrice}
                            loading={isUpdating}
                        >
                            Update
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}
